import type { HeaderStat, WidgetDataContract } from "@nexus/widget-sdk";

/**
 * `flow-geo-map` v1 — a three-stage flow graph (Sankey) correlated with a
 * world map of where its endpoints physically sit, with hover/click
 * highlighting shared live between the two.
 *
 * This pairs two chart types that already exist separately in this package
 * (`supply-chain-sankey` renders a flow graph; nothing yet renders a
 * geo-scatter map) into ONE widget rather than two, because the whole point
 * is the synchronized highlight: hovering a flow node lights up its points on
 * the map and vice versa, on every pointer move — a cross-widget filter
 * round-trip (dashboard `dataFilters`) cannot drive that without visible lag,
 * and two independently-scrolling/resizing chart widgets that must still
 * agree on one hover frame would be harder to reason about than one
 * component owning both. See "Reuse before creating a widget" in
 * `WIDGET_AUTHORING.md` — this is the "coupled interaction" exception it
 * describes, not a precedent for splitting every multi-chart section this way.
 *
 * The contract itself stays generic: nodes/links/anchors/markers, not
 * "pirate service" or "CDN provider" — a source (see
 * `normalizeProtectionOverviewCampaignPiracyIntelligence` in
 * `reportSnapshots.ts`) is what turns a specific domain's data into this
 * shape.
 */
export const FLOW_GEO_MAP_CONTRACT: WidgetDataContract = {
  name: "flow-geo-map",
  version: 1,
};

/** Flow position: `source` links only in, `sink` only out, `midpoint` both. */
export type FlowNodeKind = "source" | "midpoint" | "sink";

export interface FlowNode {
  /** Unique across the whole graph; what links join on and what markers/anchors reference. */
  id: string;
  label: string;
  kind: FlowNodeKind;
}

export interface FlowLink {
  source: string;
  target: string;
  value: number;
}

/** One fixed geographic point for a `sink`-kind node (e.g. a CDN/infra location). */
export interface GeoAnchor {
  nodeId: string;
  lng: number;
  lat: number;
}

/** One observed point attributed to both a `source`-kind and a `sink`-kind node. */
export interface GeoMarker {
  sourceNodeId: string;
  sinkNodeId: string;
  lng: number;
  lat: number;
}

export interface FlowGeoMapFreshness {
  generatedAt: string;
  dataThrough: string;
  source: string;
  isEstimated: boolean;
}

export interface FlowGeoMapData {
  nodes: FlowNode[];
  links: FlowLink[];
  anchors: GeoAnchor[];
  markers: GeoMarker[];
  freshness?: FlowGeoMapFreshness | null;
  /** Optional headline figures, shown in the header when the instance is collapsible. */
  summary?: HeaderStat[];
}
